<template>
  <div class="page-container">
    <header class="page-header">
      <div class="d-flex align-items-center">
        <portal-target name="sidebar-toggle" class="d-flex align-items-center mt-1"/>
        <h4 class="mb-0">{{ title }}</h4>
      </div>
      <div class="d-flex align-items-center">
        <b-btn v-if="canEditRoles()" class="custom-btn-primary" :disabled="isBusy" @click="showModal(null, false)">
          <b-spinner v-if="isBusy" small/>
          <v-icon name="buttons/plus" :size="12"/>
          Новая роль
        </b-btn>
      </div>
    </header>

    <div class="page-body">
      <div class="page-filters">
        <filters-drawer
          :query.sync="filters.query"
          :is-busy="isBusy"
          @submit="onSearch"
        />
      </div>

      <template v-if="isBusy || (items && items.length > 0)">
        <div class="page-table-container">
          <b-table
            id="user-roles"
            ref="user_roles"
            class="custom-table mb-0 text-small"
            sticky-header
            selectable
            select-mode="single"
            responsive
            :busy.sync="isBusy"
            :items="items"
            :fields="fields"
            no-provider-filtering
            no-provider-paging
            no-provider-sorting
            no-local-sorting
            @sort-changed="sortChanged"
            @row-dblclicked="onRowClicked"
          >
            <template #table-busy>
              <tables-skeleton :fields="fields" :rows="4"/>
            </template>

            <template #cell(active)="row">
              <div class="d-flex justify-content-center align-items-center">
                <div
                  class="ant-switch-wrapper"
                  :title="row.item.active ? 'Отключить роль' : 'Включить роль'"
                  @click.stop
                >
                  <b-form-checkbox
                    :checked="row.item.active"
                    :disabled="!canEditRoles() || isRoleUpdating(row.item.id)"
                    :aria-label="row.item.active ? 'Отключить роль' : 'Включить роль'"
                    switch
                    class="m-0"
                    @click.native.stop
                    @change="onActiveChange(row.item, $event)"
                  />

                  <div class="switch-icons-overlay">
                    <v-icon v-show="row.item.active" name="buttons/check" class="ml-2" :size="11" style="color: #fff"/>
                    <span v-show="!row.item.active" class="flex-grow-1"/>
                    <v-icon v-show="!row.item.active" name="buttons/cross" class="mr-2" :size="10" style="color: #fff"/>
                  </div>
                </div>
              </div>
            </template>

            <template #cell(action)="row">
              <b-dropdown variant="link" toggle-class="text-decoration-none box-shadow-none" no-caret class="custom-dropdown">
                <template #button-content>
                  <v-icon name="buttons/ellipsis_vertical" :size="15"/>
                </template>
                <b-dropdown-item variant="success" @click.stop="showModal(row.item.id, true)">
                  <v-icon name="buttons/eye" :size="14" class="mr-2"/>
                  Смотреть
                </b-dropdown-item>
                <b-dropdown-item v-if="canEditRoles()" variant="primary" @click="showModal(row.item.id, false)">
                  <v-icon name="buttons/edit" :size="14" class="mr-2"/>
                  Редактировать
                </b-dropdown-item>
                <b-dropdown-item v-if="canEditRoles()" variant="danger" @click="onRemove(row)">
                  <v-icon name="buttons/trash" :size="14" class="mr-2"/>
                  Удалить
                </b-dropdown-item>
              </b-dropdown>
            </template>
          </b-table>
        </div>

        <div class="page-pagination">
          <pagination-footer
            use-router
            :current-page.sync="currentPage"
            :total-rows="totalRows"
            :per-page.sync="perPage"
            @page-click="onPagination"
            @per-page-change="onPerPageChange"
          />
        </div>
      </template>

      <div v-else class="empty-state-container">
        <v-icon name="navbar/empty_table" :size="90"/>
        <h5>{{ isFiltered ? 'Нет данных соответствующих фильтрам.' : 'Нет созданных ролей.' }}</h5>
        <div v-if="!isFiltered">
          <p>Создайте новую роль прямо сейчас</p>
          <b-btn v-if="canEditRoles()" class="custom-btn-primary mt-1" :disabled="isBusy" @click="showModal(null, false)">
            <v-icon name="buttons/plus" :size="12" class="mr-1"/>
            Новая роль
          </b-btn>
        </div>
      </div>
    </div>

    <UserRole ref="user_role" @refresh="getItems" @modal-closed="onModalClosed"/>
  </div>
</template>

<script>
import roles from "../../network/roles";
  import helpers from "../../utilites/helpers";
  import FiltersDrawer from "../../utilites/commonUi/FiltersDrawer.vue";
  import permissions, {PERM} from "../../utilites/permissions";
  import PaginationFooter from "../../utilites/commonUi/PaginationFooter.vue";
  import pageSearchService from "../../utilites/pageSearchService";
  import TablesSkeleton from "../../utilites/commonUi/TablesSkeleton.vue";
    import UserRole from "./UserRole.vue";

export default {
  name: "UserRoles",
  components: { TablesSkeleton, PaginationFooter, FiltersDrawer, UserRole },
  mixins: [
    roles,
    helpers, permissions, pageSearchService
  ],
  data() {
    return {
      title: "Роли",
      isBusy: false,
      items: [],
      fields: [
        {
          key: 'id',
          label: 'ID',
          sortable: false,
          class: 'userRolesId',
        },
        {
          key: 'name',
          label: 'Роль',
          sortable: false,
        },
        {
          key: 'active',
          label: "Активна",
          sortable: false,
          class: 'text-center'
        },
        {
          key: 'action',
          label: "Действия",
          class: 'userRolesAction',
          sortable: false,
        }
      ],
      sortBy: 'id',
      sortDesc: true,
      filters: {
        query: '',
      },
      deleteRow: {
        id: null,
        name: '',
      },
      activeRoleUpdates: {},
    }
  },
  async created() {
    this.isBusy = true;
    const query = this.$route.query;

    if (query && query.query) {
      this.filters.query = query.query;
    }

    if (this.$route.hash) {
      this.currentPage = this.getPage(this.$route.hash);
    }

    await this.onSearch();
  },
  computed: {
    isFiltered() {
      return !!this.filters.query && this.filters.query.trim().length > 0;
    },
  },
  methods: {
    showModal(userRoleId, isViewonly) {
      const canEdit = this.canEditRoles();

      if (!userRoleId) {
        if (!canEdit) return;
        this.$refs.user_role.showModal(null, false);
        return;
      }

      const isView = isViewonly || !canEdit;
      const query = isView ? { mode: 'view' } : {};

      this.$router.push({
        name: 'UserRoles',
        params: { id: userRoleId },
        query: query
      });
    },
    onModalClosed() {
      if (this.$route.params.id) {
        this.$router.replace({
          name: 'UserRoles',
          params: { id: null },
          hash: this.$route.hash,
        }).catch(()=>{});
      }
    },
    onRowClicked(item) {
      this.showModal(item.id, false);
    },
    async getItems() {
      const data = {
        page: this.currentPage,
        size: this.perPage,
        query: this.filters.query,
        sortBy: [this.sortBy],
        sortDesc: [this.sortDesc],
      }

      const result = await this.getUserRoles(data);

      if (result.data == null) {
        this.items = [];
        this.isBusy = false;
        this.totalRows = 0;
        this.showError("Что-то пошло не так. Попробуйте обновить страницу и повторить действие.");
        return;
      }

      this.items = result.data.items;
      this.totalRows = result.data.total;
      this.isBusy = false;
    },
    onRemove(row) {
      if (!this.canEditRoles()) return;

      if (row.item) {
        this.deleteRow = row.item;
      }
      const title = "Удаление роли";
      const message = `Вы действительно хотите сделать роль "${this.deleteRow.name}" неактивной?`;
      this.showModalDelete(title, message, function () {
        this.removeItem(this.deleteRow);
      }.bind(this));
    },
    async removeItem(row) {
      const result = await this.deleteUserRole(row);
      if (result.error) {
        this.showToast('Ошибка при удалении.', 'Ошибка', 'danger');
      } else {
        this.showToast('Роль отключена.', 'Успех', 'success');
      }
      await this.getItems();
    },
    isRoleUpdating(roleId) {
      return !!this.activeRoleUpdates[roleId];
    },
    async onActiveChange(role, active) {
      if (!this.canEditRoles() || this.isRoleUpdating(role.id) || active === role.active) return;

      const previousActive = role.active;
      this.$set(role, 'active', active);
      this.$set(this.activeRoleUpdates, role.id, true);

      try {
        const roleResult = await this.getUserRoleById(role.id);
        if (!roleResult || roleResult.error || !roleResult.data) {
          throw new Error('Не удалось получить данные роли.');
        }

        const result = await this.putUserRole({
          ...roleResult.data,
          active,
        });
        if (!result || result.error || !result.data) {
          throw new Error('Не удалось изменить статус роли.');
        }

        this.showToast(
          active ? 'Роль включена.' : 'Роль отключена.',
          'Успех',
          'success'
        );
      } catch (error) {
        this.$set(role, 'active', previousActive);
        this.showToast('Не удалось изменить статус роли.', 'Ошибка', 'danger');
      } finally {
        this.$delete(this.activeRoleUpdates, role.id);
      }
    },
    // Permissions
    canEditRoles() {
      return this.hasPermission([PERM.ROLE_EDIT]);
    }
  },
  watch: {
    '$route.params.id': {
      handler(newId) {
        this.$nextTick(() => {
          if (newId && this.$refs.user_role) {
            const isViewonly = this.$route.query.mode === 'view';
            this.$refs.user_role.showModal(newId, isViewonly);
          } else if (!newId && this.$refs.user_role) {
            this.$refs.user_role.hideModal();
          }
        });
      }, immediate: true
    },
  }
}
</script>
<style>
.userRolesId {
  padding-left: 10px !important;
  width: 60px;
  text-align: center;
}

.userRolesAction {
  width: 80px;
  padding-right: 10px !important;
  text-align: center;
}

#user-roles td {
  vertical-align: middle !important;
}
</style>



// WEBPACK FOOTER //
// src/components/Users/UserRoles.vue